import { AIProvider, AIRequest, AIResponse, AIError } from "./aiProvider";
import { anthropicProvider } from "./anthropicProvider";
import { openaiProvider } from "./openaiProvider";
import { geminiProvider } from "./geminiProvider";
import { mistralProvider } from "./mistralProvider";
import { groqProvider } from "./groqProvider";
import { bestModelForProvider, providerOrderForTask, isKnownTask } from "./taskModelRouter";
import type { ProviderName } from "./modelCatalog";
import { logger } from "@/system/logger";
import { isProviderCreditOrAuthError } from "@/server/lib/providerCreditError";
import { reserveProviderCall, GenerationBudgetExceeded } from "@/server/ai/economics/generationBudget";
import { envCostUsd } from "@/server/ai/economics/generationBudget";

// MANDATE 34 P0: rough per-call USD estimate for the shared per-build budget's cost cap. The primary
// bound is the call COUNT; this makes the cost cap meaningful for aiRouter calls too. Env-overridable.
// Read through envCostUsd: Number("$0.05") is NaN and Math.max(0, NaN) is ALSO NaN, so the old clamp
// did not protect this. A NaN estimate reaches the budget, turns its running cost total into NaN, and
// every later ceiling comparison silently becomes false for the rest of the build.
const AIROUTER_EST_COST_USD = envCostUsd(process.env.NUVRA_AIROUTER_EST_COST_USD, 0.06);
// Parallel racing burns 2x credits for one result. Default to 1 (sequential failover) so a build
// never double-spends; an env can restore racing where latency matters more than cost.
const AIROUTER_RACE_COUNT = Math.max(1, Number(process.env.NUVRA_AIROUTER_RACE_COUNT ?? 1));

/**
 * Classify why EVERY provider attempt failed, so a build reports its real cause.
 *
 * The router used to return `{ files: [] }` with no error on total failure, so the only message an
 * operator ever saw was the caller's generic "AI returned no files - check your prompt and API key".
 * A rejected key, an exhausted credit balance, a 429, a 529 overload and a timeout all arrived as
 * that one line, which points at the prompt and the key even when the account is simply out of
 * credit. The build outcome is unchanged (it still fails); this restores the diagnosis.
 *
 * Ordered most-actionable first: credit and auth are the operator's to fix, whereas overload,
 * rate limiting and timeouts are worth retrying. Pure and exported, so the classification is
 * testable without a provider call.
 */
export function summarizeProviderFailures(failures: AIError[]): AIError {
  if (failures.length === 0) {
    return { message: "Every AI provider attempt failed without reporting a reason.", code: "all_providers_failed", retryable: true };
  }
  const detail = failures.map(f => `${f.provider ?? "provider"}: ${f.message}`).join("; ");
  const blob = failures.map(f => `${f.code ?? ""} ${f.message}`).join(" ").toLowerCase();
  const has = (...needles: string[]) => needles.some(n => blob.includes(n));

  if (has("credit", "quota", "billing", "insufficient_quota", "payment"))
    return { message: "Nuvra could not complete the AI operation with the currently available providers.", code: "provider_credit", retryable: false };
  if (has("401", "403", "unauthorized", "forbidden", "invalid api key", "authentication"))
    return { message: "Nuvra could not connect to an available AI provider.", code: "provider_auth", retryable: false };
  if (has("429", "rate limit", "rate_limit"))
    return { message: "Nuvra is handling a high volume of AI requests. Please try again shortly.", code: "provider_rate_limit", retryable: true };
  if (has("529", "overloaded", "503", "502", "unavailable"))
    return { message: "Nuvra could not reach an AI provider right now. Please try again shortly.", code: "provider_overloaded", retryable: true };
  if (has("timeout", "timed out", "etimedout", "abort"))
    return { message: "The AI generation took too long to complete. Please try again.", code: "provider_timeout", retryable: true };
  if (has("404", "model", "not_found"))
    return { message: "Nuvra could not use the requested AI capability right now.", code: "provider_model", retryable: false };
  return { message: "Nuvra could not complete the AI operation right now. Please try again.", code: "all_providers_failed", retryable: true };
}


class AIRouter {
  /**
   * Provider availability is learned from REAL provider responses, not assumed from key presence.
   * A key proves configuration only; it does not prove that the account has usable credit/quota.
   * Creditless/auth-dead providers are temporarily benched so Nuvra immediately routes the next
   * request to another configured provider that can actually serve it.
   */
  private unavailableUntil: Record<string, number> = {};

  /** Test/diagnostic hook: clear learned provider cooldowns without changing production routing policy. */
  resetProviderHealthForTests(): void {
    this.unavailableUntil = {};
  }

  private providerCooldownMs(error: AIError): number {
    const message = error.message.toLowerCase();
    if (isProviderCreditOrAuthError(error.message)) {
      return Math.max(60_000, Number(process.env.NUVRA_PROVIDER_CREDIT_COOLDOWN_MS ?? 300_000));
    }
    if (error.code === "provider_rate_limit" || /429|rate.?limit/.test(message)) {
      return Math.max(5_000, Number(process.env.NUVRA_PROVIDER_RATE_LIMIT_COOLDOWN_MS ?? 30_000));
    }
    if (error.code === "provider_overloaded" || /529|503|502|overloaded|unavailable/.test(message)) {
      return Math.max(5_000, Number(process.env.NUVRA_PROVIDER_TRANSIENT_COOLDOWN_MS ?? 15_000));
    }
    return 0;
  }

  private markProviderUnavailable(provider: string, error: AIError): void {
    const cooldownMs = this.providerCooldownMs(error);
    if (cooldownMs <= 0) return;
    this.unavailableUntil[provider] = Date.now() + cooldownMs;
    logger.warn("AI provider temporarily unavailable; routing around it", {
      provider,
      cooldownMs,
      reason: error.code ?? error.message,
    });
  }

  private isProviderTemporarilyUnavailable(provider: string): boolean {
    const until = this.unavailableUntil[provider] ?? 0;
    if (until <= Date.now()) {
      if (until) delete this.unavailableUntil[provider];
      return false;
    }
    return true;
  }

  // These are provider adapters, not a priority list. Availability + task/model capability determine
  // selection. No provider is intrinsically primary and no provider is required to be funded.
  private providers: AIProvider[] = [
    anthropicProvider,
    openaiProvider,
    geminiProvider,
    mistralProvider,
    groqProvider,
  ];

  /**
   🔥 PROVIDER METRICS (SMART ROUTING BASE)
   */
  private metrics: Record<
    string,
    {
      success: number;
      fail: number;
      avgLatency: number;
    }
  > = {};

  /**
   🔥 PROVIDER COST MAP (ESTIMATION)
   */
  private costMap: Record<string, number> = {
    anthropic: 0.8,
    openai: 1.0,
    gemini: 0.6,
    mistral: 0.5,
    groq: 0.3,
  };

  /**
   🔥 CIRCUIT BREAKER STATE
   */
  private circuit: Record<
    string,
    { failures: number; lastFail: number }
  > = {};

  private getAvailableProviders(): AIProvider[] {
    return this.providers.filter(
      (p) => p.isAvailable() && !this.isProviderTemporarilyUnavailable(p.name),
    );
  }

  /**
   * The set of provider names that are currently configured and available.
   * Used to constrain task-aware model selection to providers we can reach.
   */
  private availableProviderSet(): Set<ProviderName> {
    return new Set(this.getAvailableProviders().map((p) => p.name as ProviderName));
  }

  /**
   * Resolve the request to send to a specific provider. When the caller tags
   * the request with a `task` (and does not pin a `model`), the strongest
   * configured model for that task on this provider is selected. Otherwise the
   * request is passed through unchanged, preserving legacy default-model
   * behaviour exactly.
   */
  private requestForProvider(req: AIRequest, providerName: string): AIRequest {
    if (req.model) return req;                       // explicit pin wins
    if (!req.task || !isKnownTask(req.task)) return req;
    const model = bestModelForProvider(providerName as ProviderName, req.task, {
      availableProviders: this.availableProviderSet(),
    });
    return model ? { ...req, model } : req;
  }

  /**
   * Order available providers for the request. When a task is set, providers
   * are tried in the order their best model scores for that task; otherwise the
   * legacy smart-score ordering is used.
   */
  private orderProvidersForRequest(providers: AIProvider[], req: AIRequest): AIProvider[] {
    if (req.task && isKnownTask(req.task)) {
      const order = providerOrderForTask(req.task, { availableProviders: this.availableProviderSet() });
      return [...providers].sort((a, b) => {
        const ra = order.indexOf(a.name as ProviderName);
        const rb = order.indexOf(b.name as ProviderName);
        const pa = ra === -1 ? Number.MAX_SAFE_INTEGER : ra;
        const pb = rb === -1 ? Number.MAX_SAFE_INTEGER : rb;
        if (pa !== pb) return pa - pb;
        return this.scoreProvider(b.name) - this.scoreProvider(a.name);
      });
    }
    return [...providers].sort((a, b) => this.scoreProvider(b.name) - this.scoreProvider(a.name));
  }

  /**
   🔥 CAPABILITY FILTER (SMART SELECTION)
   */
  private filterByCapabilities(
    providers: AIProvider[],
    req: AIRequest
  ): AIProvider[] {
    return providers.filter((p) => {
      if (!p.capabilities) return true;

      if (req.tools && !p.capabilities.tools) return false;

      return true;
    });
  }

  /**
   🔥 CIRCUIT BREAKER CHECK
   */
  private isCircuitOpen(provider: string): boolean {
    const c = this.circuit[provider];
    if (!c) return false;

    // 30s cooldown
    if (c.failures >= 3 && Date.now() - c.lastFail < 30000) {
      return true;
    }

    return false;
  }

  private trackFailure(provider: string) {
    if (!this.circuit[provider]) {
      this.circuit[provider] = { failures: 0, lastFail: 0 };
    }

    this.circuit[provider].failures++;
    this.circuit[provider].lastFail = Date.now();
  }

  private resetCircuit(provider: string) {
    if (this.circuit[provider]) {
      this.circuit[provider].failures = 0;
    }
  }

  /**
   🔥 SMART SCORING (COST + PERFORMANCE)
   */
  private scoreProvider(name: string): number {
    const m = this.metrics[name] || {
      success: 1,
      fail: 0,
      avgLatency: 1,
    };

    const cost = this.costMap[name] || 1;

    return (m.success / (m.fail + 1)) * (1 / m.avgLatency) * (1 / cost);
  }

  /**
   🔥 SMART SORT (LEGACY - STILL USED INTERNALLY)
   */
  private rankProviders(providers: AIProvider[]): AIProvider[] {
    return providers.sort((a, b) => {
      return this.scoreProvider(b.name) - this.scoreProvider(a.name);
    });
  }

  /**
   🔥 METRIC TRACKING (SMOOTHED)
   */
  private track(provider: string, success: boolean, latency: number) {
    if (!this.metrics[provider]) {
      this.metrics[provider] = {
        success: 0,
        fail: 0,
        avgLatency: latency || 1,
      };
    }

    const m = this.metrics[provider];

    if (success) m.success++;
    else m.fail++;

    // Exponential smoothing
    m.avgLatency = m.avgLatency * 0.7 + latency * 0.3;
  }

  /**
   🔥 TIMEOUT WRAPPER (PREVENT HANGS)
   */
  private withTimeout<T>(promise: Promise<T>, ms = 300000): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error("Provider timeout"));
      }, ms);

      promise
        .then((res) => {
          clearTimeout(timer);
          resolve(res);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  /**
   🔥 PARALLEL RACING (TOP PROVIDERS)
   */
  private async raceProviders(
    providers: AIProvider[],
    req: AIRequest,
    /** Collects the real reason each racer failed, so a total failure can be reported
     *  honestly instead of surfacing as an empty, causeless response. */
    failures?: AIError[]
  ): Promise<AIResponse | null> {
    // MANDATE 34 P0: default 1 racer (no parallel double-spend). Only reserve racers the per-build
    // budget allows; if the budget refuses even the first, race nothing (the caller fails honestly).
    const wanted = providers.slice(0, AIROUTER_RACE_COUNT);
    const racers: AIProvider[] = [];
    for (const p of wanted) {
      try { reserveProviderCall(AIROUTER_EST_COST_USD); racers.push(p); }
      catch (e) { if (e instanceof GenerationBudgetExceeded) break; throw e; }
    }
    if (racers.length === 0) return null;

    return new Promise((resolve) => {
      let resolved = false;
      let settled = 0;
      let safety: ReturnType<typeof setTimeout> | null = null;

      // Resolve once: a winner with files, or null when every racer has failed
      // or returned empty. Falling through the instant the last racer settles is
      // essential for fast cross-provider failover - a billing/credit error
      // returns empty immediately, and waiting on the safety timer would stall
      // the whole build before the sequential fallback loop can try the rest.
      const finish = (value: AIResponse | null) => {
        if (resolved) return;
        resolved = true;
        if (safety) clearTimeout(safety);
        resolve(value);
      };

      racers.forEach((provider) => {
        const start = Date.now();

        this.withTimeout(provider.generate(this.requestForProvider(req, provider.name)), 300000)
          .then((res) => {
            if (res?.files?.length) {
              this.track(provider.name, true, Date.now() - start);
              this.resetCircuit(provider.name);
              logger.info(`🏁 Race winner: ${provider.name}`);
              finish(res);
              return;
            }
            // Empty response = soft failure (e.g. no credit, non-JSON output).
            this.track(provider.name, false, Date.now() - start);
            const failure: AIError = {
              message: res?.error?.message || "returned no files",
              code: res?.error?.code,
              provider: provider.name,
              retryable: res?.error?.retryable,
            };
            failures?.push(failure);
            this.markProviderUnavailable(provider.name, failure);
            settled++;
            if (settled >= racers.length) finish(null);
          })
          .catch((err) => {
            this.trackFailure(provider.name);
            // Keep the real cause (auth, rate limit, quota, timeout). Discarding it here is what
            // made every total failure read as "check your prompt and API key".
            const failure: AIError = {
              message: err instanceof Error ? err.message : String(err),
              provider: provider.name,
            };
            failures?.push(failure);
            this.markProviderUnavailable(provider.name, failure);
            settled++;
            if (settled >= racers.length) finish(null);
          });
      });

      // Safety ceiling for a genuinely hung provider that never settles.
      safety = setTimeout(() => finish(null), 301000);
    });
  }

  /**
   🔥 GENERATE (GOD-LEVEL)
   */
  async generate(req: AIRequest): Promise<AIResponse> {
    let providers = this.getAvailableProviders();
    // Every reason a provider attempt failed, so a total failure reports its real cause rather
    // than an empty response the caller can only describe as "no files".
    const failures: AIError[] = [];

    if (providers.length === 0) {
      logger.error("❌ No AI providers available");
      return { files: [], error: { message: "No AI provider is configured or reachable.", code: "no_providers", retryable: false } };
    }

    /**
     🔥 STEP 1 - REMOVE BROKEN PROVIDERS
     */
    providers = providers.filter((p) => !this.isCircuitOpen(p.name));

    /**
     🔥 STEP 2 - CAPABILITY FILTER
     */
    providers = this.filterByCapabilities(providers, req);

    /**
     🔥 STEP 3 - RANK (TASK-AWARE WHEN A TASK IS SET, ELSE SMART SCORING)
     */
    providers = this.orderProvidersForRequest(providers, req);

    /**
     🔥 STEP 4 - PARALLEL RACE
     */
    const raced = await this.raceProviders(providers, req, failures);
    if (raced) return raced;

    /**
     🔥 STEP 5 - FALLBACK LOOP (YOUR ORIGINAL LOGIC PRESERVED)
     */
    for (const provider of providers) {
      if (this.isProviderTemporarilyUnavailable(provider.name)) continue;
      const start = Date.now();

      // MANDATE 34 P0: reserve this call against the shared per-build budget. When the budget is
      // exhausted, stop trying providers (no more money spent) and fail honestly.
      try { reserveProviderCall(AIROUTER_EST_COST_USD); }
      catch (e) {
        if (e instanceof GenerationBudgetExceeded) {
          logger.warn(`⛔ Generation budget reached (${e.reason}); stopping provider attempts`);
          return { files: [], error: { message: e.message } } as AIResponse;
        }
        throw e;
      }

      try {
        logger.info(`🤖 Trying: ${provider.name}`);

        const res = await this.withTimeout(
          provider.generate(this.requestForProvider(req, provider.name)),
          300000
        );

        const latency = Date.now() - start;

        if (res?.files?.length) {
          this.track(provider.name, true, latency);
          this.resetCircuit(provider.name);
          return res;
        }

        this.track(provider.name, false, latency);
        const failure: AIError = {
          message: res?.error?.message || "returned no files",
          code: res?.error?.code,
          provider: provider.name,
          retryable: res?.error?.retryable,
        };
        failures.push(failure);
        this.markProviderUnavailable(provider.name, failure);
      } catch (err) {
        this.trackFailure(provider.name);
        this.track(provider.name, false, Date.now() - start);
        const failure: AIError = {
          message: err instanceof Error ? err.message : String(err),
          provider: provider.name,
        };
        failures.push(failure);
        this.markProviderUnavailable(provider.name, failure);

        logger.warn(`⚠️ Failed: ${provider.name}`, {
          error: String(err),
        });
      }
    }

    logger.error("❌ All providers failed");

    // Report WHY. An empty response with no error made every distinct cause (auth rejected, rate
    // limited, out of credit, overloaded, timed out) reach the operator as the single misleading
    // line "AI returned no files - check your prompt and API key". The build still fails exactly as
    // before; only the diagnosis it carries changes.
    return { files: [], error: summarizeProviderFailures(failures) };
  }

  /**
   🔥 STREAM (SMART + FALLBACK + SAFE)
   */
  async stream(
    req: AIRequest,
    onChunk: (chunk: any) => void
  ) {
    let providers = this.getAvailableProviders();

    if (providers.length === 0) {
      onChunk({ done: true });
      return;
    }

    /**
     🔥 CIRCUIT FILTER
     */
    providers = providers.filter((p) => !this.isCircuitOpen(p.name));

    /**
     🔥 CAPABILITY FILTER (STREAM ONLY)
     */
    providers = providers.filter((p) => p.stream);

    providers = this.orderProvidersForRequest(providers, req);

    for (const provider of providers) {
      if (!provider.stream) continue;

      const start = Date.now();
      let received = false;

      try {
        logger.info(`⚡ Streaming from: ${provider.name}`);

        await this.withTimeout(
          provider.stream(this.requestForProvider(req, provider.name), (chunk) => {
            if (chunk?.content) received = true;
            onChunk(chunk);
          }),
          25000
        );

        const latency = Date.now() - start;

        this.track(provider.name, received, latency);

        if (received) {
          this.resetCircuit(provider.name);
          return;
        }
      } catch (err) {
        this.trackFailure(provider.name);
        this.track(provider.name, false, Date.now() - start);

        logger.warn(`⚠️ Stream failed: ${provider.name}`);
      }
    }

    /**
     🔥 FALLBACK → GENERATE → STREAM SIMULATION
     */
    try {
      logger.info("🔁 Falling back to generate()");

      const res = await this.generate(req);
      const text = JSON.stringify(res);

      for (let i = 0; i < text.length; i++) {
        onChunk({ content: text[i] });
      }

      onChunk({ done: true });
    } catch {
      onChunk({ done: true });
    }
  }
}

export const aiRouter = new AIRouter();

/** Test-only reset. Production health state is intentionally process-local and learned from real calls. */
export function resetAiRouterProviderHealthForTests(): void {
  aiRouter.resetProviderHealthForTests();
}